'use client';

import { signIn } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore, useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@eliadmin/core/src/components/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { z } from 'zod';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { DialogClose, useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { SignInDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
export const signInSchema = (VM: FormValidationMessages) =>
  z.object({
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    password: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    'g-recaptcha-response': z.string().default(''),
  });

type SignInFormProps = {
  onSuccess?: () => void;
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  inDialog?: boolean;
  hideHeader?: boolean;
};

export function SignInForm({
  onSuccess,
  initialSocialAuthProviders,
  inDialog,
  hideHeader = false,
}: SignInFormProps) {
  const { t } = useTranslations();
  const me = useCustomerStore(s => s.me);
  const isInitialized = useCustomerStore(s => s.isInitialized);
  const { close: closeDialog } = useDialogStore();
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    if (me && isInitialized && !onSuccess) {
      if (inDialog) {
        router.push(pathname);
      } else {
        router.push(ROUTES.PROFILE());
      }
      closeDialog(SignInDialog.id);
    }
  }, [closeDialog, inDialog, isInitialized, me, onSuccess, pathname, router]);

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof signInSchema>>>({
    resolver: zodResolver(signInSchema(formValidationMessages)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [showPassword, setShowPassword] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);

  const TitleTag = inDialog ? 'h3' : 'h1';

  const handleSubmit = form.handleSubmit(async data => {
    try {

      const resp = await signIn({ ...data,  locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text656'),
        variant: 'success',
      });
      if (onSuccess) {
        onSuccess();
        return;
      }
      if (inDialog) {
        router.push(pathname);
      } else {
        router.push(ROUTES.PROFILE());
      }
      // window.location.reload();
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text594'),
        description: (e as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form className={cn('flex w-full flex-col gap-4', 'ea-sign-in-form')} onSubmit={handleSubmit}>
        {/* header */}
        {!hideHeader && (
          <div
            className={cn(
              'flex w-full flex-col items-center justify-center gap-4 sm:gap-6 lg:gap-8',
              'ea-sign-in-form__header',
            )}
          >
            <TitleTag
              className={cn(
                'text-center font-heading text-3xl font-medium tracking-tighter sm:text-4xl lg:text-5xl',
                'ea-sign-in-form__header-title',
              )}
            >
              {t('text190')}
            </TitleTag>

            <p
              className={cn(
                'w-full text-center font-heading text-sm text-black sm:text-base',
                ' ea-sign-in-form__header-prompt',
              )}
            >
              {t('text193')}&nbsp;
              {inDialog ? (
                <DialogClose>
                  <a
                    href={ROUTES.REGISTER() ?? '#'}
                    className={cn(
                      'font-heading font-medium text-primary',
                      'ea-sign-in-form__header-link',
                    )}
                  >
                    {t('text2019')}
                  </a>
                </DialogClose>
              ) : (
                <Link
                  href={ROUTES.REGISTER() ?? '#'}
                  className={cn(
                    'font-heading font-medium text-primary',
                    'ea-sign-in-form__header-link',
                  )}
                  prefetch={false}
                >
                  {t('text2019')}
                </Link>
              )}
            </p>
          </div>
        )}
        {/* content */}
        <SocialAuthButtons
          className="ea-sign-in-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
        />

        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-4',
            'ea-sign-in-form__content',
          )}
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem
                required
                className={cn(
                  'flex w-full flex-col items-start justify-start gap-2',
                  'ea-sign-in-form__email-field',
                )}
              >
                <FormLabel className={cn('', 'ea-sign-in-form__email-label')}>
                  {t('text35')}
                </FormLabel>
                <FormControl className={cn('', 'ea-sign-in-form__email-input')}>
                  <Input
                    {...field}
                    placeholder={t('text259')}
                    className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                  />
                </FormControl>
                <FormMessage className={cn('', 'ea-sign-in-form__email-message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem
                required
                className={cn(
                  'flex w-full flex-col items-start justify-start gap-2',
                  'ea-sign-in-form__password-field',
                )}
              >
                <FormLabel className={cn('', 'ea-sign-in-form__password-label')}>
                  {t('text191')}
                </FormLabel>
                <FormControl className={cn('w-full', 'ea-sign-in-form__password-input')}>
                  <div className="relative w-full">
                    <Input
                      placeholder={t('text191')}
                      className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                      type={showPassword ? 'text' : 'password'}
                      after={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onMouseDown={handlePasswordMouseDown}
                          onMouseUp={handlePasswordMouseUp}
                          onMouseLeave={handlePasswordMouseUp} // In case the cursor leaves the button area while pressing
                          onTouchStart={handlePasswordMouseDown}
                          onTouchEnd={handlePasswordMouseUp}
                        >
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </Button>
                      }
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage className={cn('', 'ea-sign-in-form__password-message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* FOOTER */}
        <div
          className={cn(
            'flex flex-col items-center justify-center gap-6 lg:gap-8',
            'ea-sign-in-form__footer',
          )}
        >
          {/* <div
              className={cn(
                'flex flex-row items-center space-x-2',
                'ea-sign-in-form__checkbox-wrapper',
              )}
            >
              <Checkbox id="remember-me" className={cn('', 'ea-sign-in-form__checkbox')} />
              <Label htmlFor="remember-me" className={cn('', 'ea-sign-in-form__checkbox-label')}>
                {t('text260')}
              </Label>
            </div> */}
          <Button
            type="submit"
            className={cn('w-full sm:w-fit', 'ea-sign-in-form__submit-button')}
            variant={'dark'}
            isLoading={form.formState.isSubmitting}
          >
            {t('text189')}
          </Button>
          <Link
            className={cn(
              'w-full max-w-[400px] text-center font-heading text-sm font-bold text-primary sm:text-base',
              'ea_sign-in-form__password-reset-link ',
            )}
            href={ROUTES.PASSWORD_RESET() ?? '#'}
            prefetch={false}
          >
            {t('text188')}
          </Link>
        </div>
      </form>
    </Form>
  );
}
